import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import AntToolbar from './AntToolbar';
import AntLocalization from '../utils/AntLocalization';
import AntWidgetSkeleton from './AntWidgetSkeleton';
import useWidgetFirstLoad from './AntWidgetSkeleton/useWidgetFirstLoad';

var DAY_LABELS = [
    { vi: 'T2', en: 'Mon', fr: 'Lun', es: 'Lun', id: 'Sen', ja: '月', zh: '一', ko: '월', hi: 'सोम', ar: 'اثن' },
    { vi: 'T3', en: 'Tue', fr: 'Mar', es: 'Mar', id: 'Sel', ja: '火', zh: '二', ko: '화', hi: 'मंगल', ar: 'ثلا' },
    { vi: 'T4', en: 'Wed', fr: 'Mer', es: 'Mié', id: 'Rab', ja: '水', zh: '三', ko: '수', hi: 'बुध', ar: 'أرب' },
    { vi: 'T5', en: 'Thu', fr: 'Jeu', es: 'Jue', id: 'Kam', ja: '木', zh: '四', ko: '목', hi: 'गुरु', ar: 'خمي' },
    { vi: 'T6', en: 'Fri', fr: 'Ven', es: 'Vie', id: 'Jum', ja: '金', zh: '五', ko: '금', hi: 'शुक्र', ar: 'جمع' },
    { vi: 'T7', en: 'Sat', fr: 'Sam', es: 'Sáb', id: 'Sab', ja: '土', zh: '六', ko: '토', hi: 'शनि', ar: 'سبت' },
    { vi: 'CN', en: 'Sun', fr: 'Dim', es: 'Dom', id: 'Min', ja: '日', zh: '日', ko: '일', hi: 'रवि', ar: 'أحد' }
];

var MONTH_LABELS = [
    { vi: 'Th1', en: 'Jan', fr: 'Jan', es: 'Ene', id: 'Jan', ja: '1月', zh: '1月', ko: '1월', hi: 'जन', ar: 'يناير' },
    { vi: 'Th2', en: 'Feb', fr: 'Fév', es: 'Feb', id: 'Feb', ja: '2月', zh: '2月', ko: '2월', hi: 'फर', ar: 'فبراير' },
    { vi: 'Th3', en: 'Mar', fr: 'Mar', es: 'Mar', id: 'Mar', ja: '3月', zh: '3月', ko: '3월', hi: 'मार्च', ar: 'مارس' },
    { vi: 'Th4', en: 'Apr', fr: 'Avr', es: 'Abr', id: 'Apr', ja: '4月', zh: '4月', ko: '4월', hi: 'अप्रैल', ar: 'أبريل' },
    { vi: 'Th5', en: 'May', fr: 'Mai', es: 'May', id: 'Mei', ja: '5月', zh: '5月', ko: '5월', hi: 'मई', ar: 'مايو' },
    { vi: 'Th6', en: 'Jun', fr: 'Jun', es: 'Jun', id: 'Jun', ja: '6月', zh: '6月', ko: '6월', hi: 'जून', ar: 'يونيو' },
    { vi: 'Th7', en: 'Jul', fr: 'Jul', es: 'Jul', id: 'Jul', ja: '7月', zh: '7月', ko: '7월', hi: 'जुल', ar: 'يوليو' },
    { vi: 'Th8', en: 'Aug', fr: 'Aoû', es: 'Ago', id: 'Agu', ja: '8月', zh: '8月', ko: '8월', hi: 'अग', ar: 'أغسطس' },
    { vi: 'Th9', en: 'Sep', fr: 'Sep', es: 'Sep', id: 'Sep', ja: '9月', zh: '9月', ko: '9월', hi: 'सित', ar: 'سبتمبر' },
    { vi: 'Th10', en: 'Oct', fr: 'Oct', es: 'Oct', id: 'Okt', ja: '10月', zh: '10月', ko: '10월', hi: 'अक्टू', ar: 'أكتوبر' },
    { vi: 'Th11', en: 'Nov', fr: 'Nov', es: 'Nov', id: 'Nov', ja: '11月', zh: '11月', ko: '11월', hi: 'नव', ar: 'نوفمبر' },
    { vi: 'Th12', en: 'Dec', fr: 'Déc', es: 'Dic', id: 'Des', ja: '12月', zh: '12月', ko: '12월', hi: 'दिस', ar: 'ديسمبر' }
];

var T_CUSTOMERS = { vi: 'Khách hàng', en: 'Customers', fr: 'Clients', es: 'Clientes', id: 'Pelanggan', ja: '顧客', zh: '客户', ko: '고객', hi: 'ग्राहक', ar: 'العملاء' };
var T_ORDERS = { vi: 'Đơn hàng', en: 'Orders', fr: 'Commandes', es: 'Pedidos', id: 'Pesanan', ja: '注文', zh: '订单', ko: '주문', hi: 'ऑर्डर', ar: 'طلبات' };
var T_REVENUE = { vi: 'Doanh thu', en: 'Revenue', fr: 'Revenus', es: 'Ingresos', id: 'Pendapatan', ja: '収益', zh: '収入', ko: '매출', hi: 'राजस्व', ar: 'الإيرادات' };
var T_LOW = { vi: 'Ít', en: 'Low', fr: 'Bas', es: 'Bajo', id: 'Rendah', ja: '少', zh: '少', ko: '낮음', hi: 'कम', ar: 'منخفض' };
var T_HIGH = { vi: 'Nhiều', en: 'High', fr: 'Haut', es: 'Alto', id: 'Tinggi', ja: '多', zh: '多', ko: '높음', hi: 'अधिक', ar: 'مرتفع' };

var SCALE = [
    [241, 245, 249],
    [219, 234, 254],
    [147, 197, 253],
    [79, 142, 247],
    [37, 99, 235],
    [30, 58, 138]
];

function cellColor(ratio) {
    if (ratio <= 0) return 'rgb(241,245,249)';
    if (ratio >= 1) return 'rgb(30,58,138)';
    var pos = ratio * (SCALE.length - 1);
    var i = Math.floor(pos);
    var t = pos - i;
    var a = SCALE[i], b = SCALE[Math.min(i + 1, SCALE.length - 1)];
    return 'rgb(' + Math.round(a[0] + (b[0] - a[0]) * t) + ',' + Math.round(a[1] + (b[1] - a[1]) * t) + ',' + Math.round(a[2] + (b[2] - a[2]) * t) + ')';
}

export default function AntWidgetCalendarHeatmap(props) {
    var [rows, setRows] = useState([]);
    var [hovered, setHovered] = useState(null);
    var wrapRef = useRef(null);
    var [loaded, markLoaded] = useWidgetFirstLoad(Boolean(props.url));

    var numWeeks = props.numWeeks || 16;
    var valueField = props.valueField || 'customer_count';

    var load = useCallback(async () => {
        if (props.url) {
            props.API.post(props.url, { numWeeks, ...props.searchConditions }, (resp) => {
                if (resp && !resp.error && resp.data) {
                    var data = resp.data instanceof Array ? resp.data : resp.data.rows || [];
                    setRows(data);
                    if (props.onEmpty) props.onEmpty(data.length === 0);
                } else {
                    setRows([]);
                    if (props.onEmpty) props.onEmpty(true);
                }
                markLoaded();
            });
        }
    }, [props.API, props.url, props.searchConditions, numWeeks, markLoaded]);

    useEffect(() => {
        load();
        var interval = setInterval(load, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => { load(); }, [props.searchConditions, props.url]);

    var { weekColumns, monthMarkers, maxVal } = useMemo(() => {
        var map = {};
        var max = 0;
        for (var r of rows) {
            map[r.date_str] = r;
            var v = parseFloat(r[valueField]) || 0;
            if (v > max) max = v;
        }

        var today = new Date();
        today.setHours(12, 0, 0, 0);

        // This week's Sunday
        var dow = today.getDay(); // 0=Sun
        var thisSunday = new Date(today);
        thisSunday.setDate(today.getDate() + (dow === 0 ? 0 : 7 - dow));

        // Start Monday = thisSunday - numWeeks*7 + 1
        var startMonday = new Date(thisSunday);
        startMonday.setDate(thisSunday.getDate() - numWeeks * 7 + 1);

        var cols = [];
        var markers = [];
        var cursor = new Date(startMonday);
        var prevMonth = -1;

        for (var w = 0; w < numWeeks; w++) {
            var week = [];
            for (var d = 0; d < 7; d++) {
                var y = cursor.getFullYear();
                var m = cursor.getMonth();
                var dd = cursor.getDate();
                var ds = y + '-' + String(m + 1).padStart(2, '0') + '-' + String(dd).padStart(2, '0');
                var isFuture = cursor > today;
                week.push({ dateStr: ds, year: y, month: m, day: dd, isFuture: isFuture, data: map[ds] || null });

                if (d === 0 && m !== prevMonth) {
                    markers.push({ weekIdx: w, month: m });
                    prevMonth = m;
                }
                cursor.setDate(cursor.getDate() + 1);
            }
            cols.push(week);
        }

        return { weekColumns: cols, monthMarkers: markers, maxVal: max };
    }, [rows, valueField, numWeeks]);

    var hovCell = hovered ? hovered.cell : null;

    if (!loaded) {
        return (
            <React.Fragment>
                {props.title && <AntToolbar title={props.title} isH6={true} upperCase={true} />}
                <AntWidgetSkeleton variant="calendar" columns={numWeeks} />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {props.title && <AntToolbar title={props.title} isH6={true} upperCase={true} />}
            <div className="ant-widget-heatmap" ref={wrapRef}>
                <div className="ant-widget-heatmap__grid">
                    {/* Month labels row */}
                    <div className="ant-widget-heatmap__row ant-widget-heatmap__row--header">
                        <div className="ant-widget-heatmap__day-label" />
                        {weekColumns.map(function (_, wIdx) {
                            var mk = monthMarkers.find(function (m) { return m.weekIdx === wIdx; });
                            return (
                                <div key={wIdx} className="ant-widget-heatmap__month-label">
                                    {mk ? AntLocalization.translateText(MONTH_LABELS[mk.month]) : ''}
                                </div>
                            );
                        })}
                    </div>

                    {/* 7 day rows (Mon=0 … Sun=6) */}
                    {[0, 1, 2, 3, 4, 5, 6].map(function (dayIdx) {
                        return (
                            <div key={dayIdx} className="ant-widget-heatmap__row">
                                <div className="ant-widget-heatmap__day-label">
                                    {dayIdx % 2 === 0 ? AntLocalization.translateText(DAY_LABELS[dayIdx]) : ''}
                                </div>
                                {weekColumns.map(function (week, wIdx) {
                                    var cell = week[dayIdx];
                                    if (cell.isFuture) {
                                        return <div key={wIdx} className="ant-widget-heatmap__cell ant-widget-heatmap__cell--empty" />;
                                    }
                                    var val = cell.data ? (parseFloat(cell.data[valueField]) || 0) : 0;
                                    var ratio = maxVal > 0 ? val / maxVal : 0;
                                    var isHov = hovered && hovered.wIdx === wIdx && hovered.dIdx === dayIdx;
                                    return (
                                        <div
                                            key={wIdx}
                                            className={'ant-widget-heatmap__cell' + (isHov ? ' ant-widget-heatmap__cell--hover' : '')}
                                            style={{ background: cellColor(ratio) }}
                                            onMouseEnter={function (e) {
                                                var rect = e.currentTarget.getBoundingClientRect();
                                                var parentRect = wrapRef.current.getBoundingClientRect();
                                                setHovered({
                                                    wIdx: wIdx, dIdx: dayIdx, cell: cell,
                                                    x: rect.left - parentRect.left + rect.width / 2,
                                                    y: rect.top - parentRect.top
                                                });
                                            }}
                                            onMouseLeave={function () { setHovered(null); }}
                                        />
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>

                {/* Legend */}
                <div className="ant-widget-heatmap__legend">
                    <span className="ant-widget-heatmap__legend-label">{AntLocalization.translateText(T_LOW)}</span>
                    <div className="ant-widget-heatmap__legend-bar" />
                    <span className="ant-widget-heatmap__legend-label">{AntLocalization.translateText(T_HIGH)}</span>
                </div>

                {/* Tooltip */}
                {hovered && hovCell && !hovCell.isFuture && (
                    <div className="ant-widget-heatmap__tooltip" style={{ left: hovered.x, top: hovered.y - 4 }}>
                        <div className="ant-widget-heatmap__tooltip-time">
                            {AntLocalization.translateText(DAY_LABELS[hovered.dIdx]) + ', ' + String(hovCell.day).padStart(2, '0') + '/' + String(hovCell.month + 1).padStart(2, '0') + '/' + hovCell.year}
                        </div>
                        <div className="ant-widget-heatmap__tooltip-row">
                            <span>{AntLocalization.translateText(T_CUSTOMERS)}</span>
                            <span>{AntLocalization.translateInteger((hovCell.data && hovCell.data.customer_count) || 0)}</span>
                        </div>
                        <div className="ant-widget-heatmap__tooltip-row">
                            <span>{AntLocalization.translateText(T_ORDERS)}</span>
                            <span>{AntLocalization.translateInteger((hovCell.data && hovCell.data.order_count) || 0)}</span>
                        </div>
                        <div className="ant-widget-heatmap__tooltip-row">
                            <span>{AntLocalization.translateText(T_REVENUE)}</span>
                            <span>{AntLocalization.translateCurrency((hovCell.data && hovCell.data.revenue) || 0)}</span>
                        </div>
                    </div>
                )}
            </div>
        </React.Fragment>
    );
}
